"use client";

import { cn } from "@/lib/utils";

type Props = {
  size?: number;
  className?: string;
  /** "talking" gives the mascot a slight bob; "idle" only does the float + blink. */
  state?: "idle" | "talking" | "thinking";
};

/**
 * Wrench — the assistant mascot. Its body and eye colors are driven by the
 * `--wm-*` palette variables (globals.css), so it tracks the active palette:
 * warm clay/amber in Classic, blue-steel with cyan eyes in Cobalt. All
 * animation is CSS so it's cheap to render in many places.
 */
export function WrenchMascot({ size = 56, className, state = "idle" }: Props) {
  return (
    <span
      className={cn("relative inline-block select-none", className)}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <span
        className={cn(
          "absolute inset-0",
          state === "talking" ? "wrench-bob" : "wrench-float",
        )}
      >
        <svg
          viewBox="0 0 64 72"
          className="absolute inset-0 h-full w-full drop-shadow-[0_14px_28px_hsl(var(--accent)/0.32)]"
        >
          <defs>
            <radialGradient id="wrench-body" cx="38%" cy="32%" r="78%">
              <stop offset="0%" stopColor="hsl(var(--wm-1))" />
              <stop offset="40%" stopColor="hsl(var(--wm-2))" />
              <stop offset="78%" stopColor="hsl(var(--wm-3))" />
              <stop offset="100%" stopColor="hsl(var(--wm-4))" />
            </radialGradient>
            <radialGradient id="wrench-eye" cx="50%" cy="50%" r="65%">
              <stop offset="0%" stopColor="hsl(var(--wm-eye-1))" />
              <stop offset="55%" stopColor="hsl(var(--wm-eye-2))" />
              <stop offset="100%" stopColor="hsl(var(--wm-eye-3))" />
            </radialGradient>
            <linearGradient id="wrench-rim" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--wm-1) / 0.55)" />
              <stop offset="60%" stopColor="hsl(var(--wm-1) / 0)" />
            </linearGradient>
            <filter id="wrench-eye-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="1.4" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {/* Body — soft teardrop with a tapered tail. */}
          <path
            d="M32 4
               C16 4 6 16 6 32
               C6 46 14 56 22 62
               C26 65 28 68 32 68
               C36 68 38 65 42 62
               C50 56 58 46 58 32
               C58 16 48 4 32 4 Z"
            fill="url(#wrench-body)"
          />
          {/* Top rim sheen. */}
          <path
            d="M32 4
               C16 4 6 16 6 32
               C6 46 14 56 22 62
               C26 65 28 68 32 68
               C36 68 38 65 42 62
               C50 56 58 46 58 32
               C58 16 48 4 32 4 Z"
            fill="url(#wrench-rim)"
          />
          {/* Highlight on the top-right to give it volume. */}
          <ellipse cx="44" cy="20" rx="9" ry="4.5" fill="hsl(var(--wm-hi) / 0.45)" />
          {/* Eyes — palette-driven (amber in Classic, cyan in Cobalt). */}
          <g filter="url(#wrench-eye-glow)" className={state === "thinking" ? "wrench-eye-think" : "wrench-eye"}>
            <circle cx="24" cy="32" r="5" fill="url(#wrench-eye)" />
            <circle cx="40" cy="32" r="5" fill="url(#wrench-eye)" />
            <circle cx="22.5" cy="30.5" r="1.4" fill="hsl(var(--wm-hi))" />
            <circle cx="38.5" cy="30.5" r="1.4" fill="hsl(var(--wm-hi))" />
          </g>
          {/* Eyelids — animate to "blink" by scaling vertically. */}
          <g className="wrench-blink" style={{ transformOrigin: "32px 32px" }}>
            <rect x="18" y="27" width="12" height="0" fill="hsl(var(--wm-4))" />
            <rect x="34" y="27" width="12" height="0" fill="hsl(var(--wm-4))" />
          </g>
        </svg>
      </span>
      <style jsx>{`
        .wrench-float {
          animation: wrench-float 4.6s ease-in-out infinite;
        }
        .wrench-bob {
          animation: wrench-bob 0.6s ease-in-out infinite;
        }
        @keyframes wrench-float {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }
        @keyframes wrench-bob {
          0%,
          100% {
            transform: translateY(0) rotate(-1.5deg);
          }
          50% {
            transform: translateY(-2px) rotate(1.5deg);
          }
        }
        :global(.wrench-blink) {
          animation: wrench-blink 5.4s ease-in-out infinite;
        }
        @keyframes wrench-blink {
          0%,
          92%,
          100% {
            transform: scaleY(0);
          }
          94%,
          96% {
            transform: scaleY(1);
          }
        }
        :global(.wrench-eye-think) {
          animation: wrench-eye-think 2s ease-in-out infinite;
        }
        @keyframes wrench-eye-think {
          0%,
          100% {
            transform: translateX(-1px);
          }
          50% {
            transform: translateX(1px);
          }
        }
      `}</style>
    </span>
  );
}
